import { object, ValidationError } from "yup";

import { get, find, debounce } from "lodash";

import { REGISTRATION_STEPS } from "@tvg/atomic-ui/_static/Constants/progressBar";

import { getAge, invertDate } from "../../utils/testFns";

import {
  setAddressFieldsRegistrationAction,
  setAddressInfoScreenAction,
  setAddressValidFormRegistrationAction,
  setCurrentStepRegistrationAction,
  setPassThroughAddressAction
} from "../../actions";

import {
  homeStateValidation,
  homeStreetValidation,
  homeZipValidation,
  homeCityValidation,
  mailingStateValidation,
  mailingStreetValidation,
  mailingZipValidation,
  mailingCityValidation
} from "./utils";
import {
  getStateValidationMemo,
  getZipValidationMemo
} from "../../utils/utils";
import {
  handleInputOnTheFlyChange,
  handleSelectChangeGtm,
  handleOnTheFlyChange
} from "../../utils/handlers";
import {
  onClickContinueBtnGtmEvent,
  onFormStepSuccessGtmEvent
} from "../../utils/gtm/formCompletion";
import { onErrorInputGtmEvent } from "../../utils/gtm/inputRelated";

export const debouncedAddressUpdate = debounce(
  (formData, dispatch) =>
    dispatch(setAddressFieldsRegistrationAction(formData)),
  200
);

export const debouncedFieldErrorGtm = debounce(
  (concatenatedErrors, module, isEditing) =>
    onErrorInputGtmEvent(concatenatedErrors, module, isEditing),
  300
);

export const updateAddressInRedux = ({ formRef, dispatch }) => {
  if (formRef && formRef.current) {
    const formData = formRef.current.getData();
    debouncedAddressUpdate(formData, dispatch);
  }
};

export const updateAddressHomeInRedux = ({ dispatch, home }) => {
  if (home) {
    debouncedAddressUpdate({ home }, dispatch);
  }
};

export const handleAddressSubmit =
  ({
    dispatch,
    formRef,
    hasMailingAddress,
    isEditing,
    isFormValid,
    labels,
    setIsFormDirty,
    welcomeFields
  }) =>
  async (addressFields) => {
    setIsFormDirty(true);
    onClickContinueBtnGtmEvent(`${isEditing ? "Edit " : ""}Address`);
    const { legalWagerAge, stateDescription } = addressFields.home;

    if (!hasMailingAddress) {
      formRef.current.setFieldValue("mailing.city", "");
      formRef.current.setFieldValue("mailing.details", "");
      formRef.current.setFieldValue("mailing.state", "");
      formRef.current.setFieldValue("mailing.street", "");
      formRef.current.setFieldValue("mailing.zip", "");
    }

    updateAddressInRedux({
      formRef,
      dispatch
    });

    try {
      const schema = object().shape({
        home: object().shape({
          street: homeStreetValidation(labels),
          city: homeCityValidation(labels),
          state: homeStateValidation(labels),
          zip: homeZipValidation(labels)
        }),
        mailing:
          hasMailingAddress &&
          object().shape({
            street: mailingStreetValidation(labels),
            city: mailingCityValidation(labels),
            state: mailingStateValidation(labels),
            zip: mailingZipValidation(labels)
          })
      });

      await schema.validate(addressFields, {
        abortEarly: false
      });

      const userAge = getAge(
        invertDate(welcomeFields.birth.replace(/\s/g, "-"))
      );
      if ((!!legalWagerAge && userAge >= legalWagerAge) || !legalWagerAge) {
        // Remove all previous errors
        formRef.current.setErrors({});
        // Validation passed
        dispatch(setAddressValidFormRegistrationAction(true));
        if (isEditing) {
          dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.REVIEW));
        } else {
          dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.SSN));
        }
        onFormStepSuccessGtmEvent(`${isEditing ? "Edit " : ""}Address`);
      } else {
        dispatch(
          setAddressInfoScreenAction({
            isOpen: true,
            status: "ageError",
            state: stateDescription,
            ageLimit: legalWagerAge
          })
        );
      }
    } catch (err) {
      const validationErrors = {};
      if (err instanceof ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
        // Set invalid form in redux if not invalid yet
        if (isFormValid) {
          dispatch(setAddressValidFormRegistrationAction(false));
        }

        const concatenatedErrors = Object.values(validationErrors).reduce(
          (accumulator, error, index) => {
            if (index === 0) {
              return error;
            }
            return `${accumulator}, ${error}`;
          },
          ""
        );

        // Send GTM of inputs with errors concatenated
        debouncedFieldErrorGtm(concatenatedErrors, module, isEditing);
      }
    }
  };

export const onHomeStateChangeFn =
  ({ formRef, birth, dispatch }) =>
  ({ selectOptions, value, validWageringState, redirectUrl }) => {
    const stateAbbreviation = value;
    const stateDescription = get(
      find(selectOptions, (item) => item.value === value),
      "label",
      ""
    );

    return getStateValidationMemo(stateAbbreviation)
      .then((result) => {
        if (validWageringState === false) {
          dispatch(
            setAddressInfoScreenAction({
              isOpen: true,
              status: "invalidWageringState",
              state: stateDescription
            })
          );
        } else {
          const { legalWagerAge } = result.data;
          const { home } = formRef.current.getData();
          updateAddressHomeInRedux({
            dispatch,
            home: { ...home, legalWagerAge, stateDescription }
          });
          const userAge = getAge(invertDate(birth.replace(/\s/g, "-")));
          if (redirectUrl) {
            dispatch(
              setAddressInfoScreenAction({
                isOpen: true,
                status: "brandError",
                state: stateDescription,
                ageLimit: legalWagerAge,
                redirectUrl
              })
            );
          } else if (userAge < legalWagerAge) {
            dispatch(
              setAddressInfoScreenAction({
                isOpen: true,
                status: "ageError",
                state: stateDescription,
                ageLimit: legalWagerAge
              })
            );
            dispatch(setPassThroughAddressAction(true));
          } else {
            formRef.current.setFieldError("home.state", "");
          }
        }
      })
      .catch(() => {
        getStateValidationMemo.cache.delete(stateAbbreviation);
      });
  };

export const streetOnChangeFn =
  ({ isFormDirty, validation, formRef, dispatch, isEditing }) =>
  (value, fieldName) => {
    handleOnTheFlyChange(
      isFormDirty,
      fieldName,
      value,
      validation,
      formRef,
      "Address",
      isEditing
    );
    updateAddressInRedux({ formRef, dispatch });
  };

export const onHomeZipChange = ({ formRef, dispatch, welcomeFields, zip }) => {
  if (zip && !zip.includes("_")) {
    return getZipValidationMemo(zip)
      .then((result) => {
        const status = get(result, "data.status");

        if (status === "success") {
          const stateAbbreviation = get(result, "data.response.StateAbbr");
          const city = get(result, "data.response.City", "").trim();
          const redirectUrl = get(result, "data.response.redirectUrl", "");
          const validWageringState = get(
            result,
            "data.response.validWageringState",
            false
          );

          return onHomeStateChangeFn({
            formRef,
            birth: welcomeFields.birth,
            dispatch
          })({
            selectOptions: formRef.current.getFieldRef("home.state").options,
            value: stateAbbreviation,
            validWageringState,
            redirectUrl
          }).then(() => {
            formRef.current.setFieldValue("home.city", city);
            formRef.current.setFieldValue("home.state", stateAbbreviation);
            formRef.current.setFieldError("home.city", "");
            formRef.current.setFieldValue("home.zip", zip);
            formRef.current.setFieldError("home.zip", "");
            updateAddressInRedux({ formRef, dispatch });
          });
        }
        formRef.current.setFieldValue("home.city", "");
        formRef.current.setFieldValue("home.state", "");
        return Promise.resolve(result);
      })
      .catch(() => {
        getZipValidationMemo.cache.delete(zip);
      });
  }

  return Promise.resolve({ data: { status: "error" } });
};

export const onMailingZipChange = ({ formRef, dispatch, zip }) => {
  if (zip && !zip.includes("_")) {
    return getZipValidationMemo(zip)
      .then((result) => {
        const status = get(result, "data.status");
        if (status === "success") {
          const stateAbbreviation = get(result, "data.response.StateAbbr");
          const city = get(result, "data.response.City", "").trim();
          formRef.current.setFieldValue("mailing.city", city);
          formRef.current.setFieldValue("mailing.state", stateAbbreviation);
          formRef.current.setFieldError("mailing.city", "");
          formRef.current.setFieldError("mailing.state", "");
          formRef.current.setFieldError("mailing.zip", "");
          updateAddressInRedux({ formRef, dispatch });
        } else {
          formRef.current.setFieldValue("mailing.city", "");
          formRef.current.setFieldValue("mailing.state", "");
        }
      })
      .catch(() => {
        getZipValidationMemo.cache.delete(zip);
      });
  }

  return Promise.resolve({ data: { status: "error" } });
};

export const cityOnChangeFn =
  ({ isFormDirty, validation, formRef, dispatch, isEditing }) =>
  (value, fieldName) => {
    handleInputOnTheFlyChange(
      isFormDirty,
      fieldName,
      value,
      validation,
      formRef,
      "Address",
      isEditing
    );
    updateAddressInRedux({ formRef, dispatch });
  };

export const onCheckMailing = (value, isEditing) => {
  handleSelectChangeGtm(
    value === "false" ? "mailingSelect" : "mailingUnselect",
    isEditing,
    "Address",
    true
  );
};
